export const BRAND_THEME_STORAGE_KEY = 'brand-theme';
export const BRAND_THEME_VERSION_STORAGE_KEY = 'brand-theme-version';
export const BRAND_THEME_VERSION = '3';
export const DEFAULT_BRAND_THEME_KEY = 'amber';

export const BRAND_THEMES = Object.freeze([
    {
        key: 'ocean',
        labelKey: 'brand_theme_ocean',
        primary: '#0f6fde',
        hover: '#0c60c4',
        pressed: '#0a56af',
        gradientEnd: '#3896ff',
    },
    {
        key: 'emerald',
        labelKey: 'brand_theme_emerald',
        primary: '#0f9f75',
        hover: '#0b8c65',
        pressed: '#097654',
        gradientEnd: '#35c494',
    },
    {
        key: 'amber',
        labelKey: 'brand_theme_amber',
        primary: '#f97316',
        hover: '#ea580c',
        pressed: '#c2410c',
        gradientEnd: '#fb923c',
    },
    {
        key: 'ruby',
        labelKey: 'brand_theme_ruby',
        primary: '#c2415d',
        hover: '#a93550',
        pressed: '#8b2a42',
        gradientEnd: '#ea6c87',
    },
    {
        key: 'violet',
        labelKey: 'brand_theme_violet',
        primary: '#6d5df6',
        hover: '#5a4ce0',
        pressed: '#4c3fc2',
        gradientEnd: '#968cff',
    },
    {
        key: 'teal',
        labelKey: 'brand_theme_teal',
        primary: '#0f9f8a',
        hover: '#0b8c79',
        pressed: '#097466',
        gradientEnd: '#2ec4b6',
    },
    {
        key: 'indigo',
        labelKey: 'brand_theme_indigo',
        primary: '#4256d0',
        hover: '#394abd',
        pressed: '#2f3e9f',
        gradientEnd: '#7484ff',
    },
    {
        key: 'copper',
        labelKey: 'brand_theme_copper',
        primary: '#b96a2e',
        hover: '#a45d27',
        pressed: '#8a4c1f',
        gradientEnd: '#de965e',
    },
]);

const getThemeByKey = (themeKey = DEFAULT_BRAND_THEME_KEY) => {
    return BRAND_THEMES.find((theme) => theme.key === themeKey) || BRAND_THEMES[0];
};

export const normalizeBrandThemeKey = (themeKey = DEFAULT_BRAND_THEME_KEY) => {
    return getThemeByKey(themeKey).key;
};

const hexToRgb = (hex) => {
    const normalized = hex.replace('#', '');
    const value = normalized.length === 3
        ? normalized.split('').map((item) => item + item).join('')
        : normalized;
    const numericValue = Number.parseInt(value, 16);

    return {
        r: (numericValue >> 16) & 255,
        g: (numericValue >> 8) & 255,
        b: numericValue & 255,
    };
};

const setCssVariables = (theme) => {
    if (typeof document === 'undefined') {
        return theme.key;
    }

    const htmlElement = document.documentElement;
    const { r, g, b } = hexToRgb(theme.primary);

    htmlElement.setAttribute('brand-theme', theme.key);
    htmlElement.style.setProperty('--brand-primary', theme.primary);
    htmlElement.style.setProperty('--brand-primary-hover', theme.hover);
    htmlElement.style.setProperty('--brand-primary-pressed', theme.pressed);
    htmlElement.style.setProperty('--brand-gradient-end', theme.gradientEnd);
    htmlElement.style.setProperty('--brand-primary-rgb', `${r}, ${g}, ${b}`);

    return theme.key;
};

export const getStoredBrandTheme = () => {
    if (typeof window === 'undefined') {
        return DEFAULT_BRAND_THEME_KEY;
    }

    const storedTheme = window.localStorage.getItem(BRAND_THEME_STORAGE_KEY);
    const storedVersion = window.localStorage.getItem(BRAND_THEME_VERSION_STORAGE_KEY);

    if (storedVersion !== BRAND_THEME_VERSION && (!storedTheme || storedTheme === 'ocean')) {
        return DEFAULT_BRAND_THEME_KEY;
    }

    return getThemeByKey(storedTheme).key;
};

export const applyBrandTheme = (themeKey) => {
    const theme = getThemeByKey(themeKey);
    const appliedKey = setCssVariables(theme);

    if (typeof window !== 'undefined') {
        window.localStorage.setItem(BRAND_THEME_STORAGE_KEY, appliedKey);
        window.localStorage.setItem(BRAND_THEME_VERSION_STORAGE_KEY, BRAND_THEME_VERSION);
    }

    return appliedKey;
};

export const initializeBrandTheme = (store = null) => {
    const appliedKey = applyBrandTheme(getStoredBrandTheme());

    if (store?.state?.theme) {
        if (typeof store.commit === 'function') {
            store.commit('SET_BRAND_THEME', appliedKey);
        } else {
            store.state.theme.brandTheme = appliedKey;
        }
    }

    return appliedKey;
};

export const getBrandThemeOptions = () => BRAND_THEMES.map((theme) => ({
    ...theme,
}));
